import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import 'leaflet';
import { Observable } from 'rxjs/Observable';

import { Contract, Siterecord } from 'app/models';
import { ContractType, DataProviderType, StorageKey } from 'app/models/enums';
import { AuthorizationService, MapService } from 'app/services';

declare const L: any;

@Component({
  selector: 'locations-map',
  templateUrl: './locations-map.component.html',
  styleUrls: ['./locations-map.component.scss']
})
export class LocationsMapComponent implements OnInit {
  @Input() locations: Siterecord[];
  @Input() contract: Contract;

  @Output() goToLocationdetailPage: EventEmitter<Siterecord> = new EventEmitter<Siterecord>();
  @Output() goToLocationEditPage: EventEmitter<Siterecord> = new EventEmitter<Siterecord>();

  public loadingMap = false;
  private contractId: number;
  private map: L.Map;
  private control: any;
  private legend: L.Control;

  private defaultBaseLayer: L.TileLayer;
  private alternativeBaseLayers: L.TileLayer[];
  private activeBaseLayerId: string;

  private hectoPuntenLayer: L.TileLayer;
  private wmsOtherLinearsLayer: L.TileLayer;
  private wmsOtherFcdLinearsLayer: L.TileLayer;
  private wmsContractLinearsLayer: L.TileLayer;
  private wmsOtherPointsLayer: L.TileLayer;
  private wmsContractPointsLayer: L.TileLayer;
  private wmsAllinearsLayer: L.TileLayer;
  private wmsAllPointsLayer: L.TileLayer;
  private wmsAllFcdLinearsLayer: L.TileLayer;
  private activeLayerNames: string[];

  private defaultBaseLayerTranslation: string;
  private hectopuntenLayerNameTranslation: string;
  private otherLinearLayerNameTranslation: string;
  private otherFcdLinearLayerNameTranslation: string;
  private otherPointLayerNametranslation: string;
  private contractLinearLayerNameTranslation: string;
  private contractPointLayerNameTranslation: string;
  private allLinearLayerNametranslation: string;
  private allPointLayerNameTranslation: string;
  private allFcdLinearLayerNameTranslation: string;

  private legendActiveTranslation: string;
  private legendInacitveTranslation: string;
  private legendOthersTranslation: string;

  private DEFAULT_BASE_LAYER_ID = 'default';
  private HECTOPUNTEN_LAYERNAME = 'siterecord:hectopunten';
  private ALL_LINEARS_LAYERNAME = 'siterecord:allLinears';
  private ALL_POINTS_LAYERNAME = 'siterecord:allPoints';
  private ALL_FCD_LINEARS_LAYERNAME = 'siterecord:allFcdLinears';
  private OTHER_POINTS_LAYERNAME = 'siterecord:otherPoints';
  private CONTRACT_POINTS_LAYERNAME = 'siterecord:contractPoints';
  private OTHER_LINEARS_LAYERNAME = 'siterecord:otherLinears';
  private CONTRACT_LINEARS_LAYERNAME = 'siterecord:contractLinears';
  private OTHER_FCD_LINEARS_LAYERNAME = 'siterecord:otherFcdLinears';

  constructor(
    private readonly mapService: MapService,
    private readonly translateService: TranslateService,
    private readonly authorizationService: AuthorizationService
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.contract) {
      this.contractId = this.contract.id;
    }
    this.initialiseLayerNames();
    this.initialiseLegendLabels();
    await this.initialiseMap();
    this.initialiseControl();
    this.initialiseLegend();

    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.initialiseLayerNames();
      this.initialiseLegendLabels();
      this.initialiseControl();
      this.initialiseLegend();
    });
  }

  private async initialiseMap(): Promise<void> {
    this.loadingMap = true;
    let centerMap: any;
    let zoomLevel: number;
    this.alternativeBaseLayers = [];
    this.activeLayerNames = [];

    if (localStorage.getItem(StorageKey.MAP_OPTIONS)) {
      const latlng: L.LatLng = this.getItem('latlng');
      const zoom: number = this.getItem('zoomLevel');

      centerMap = [latlng.lat, latlng.lng];
      zoomLevel = zoom;

      this.activeBaseLayerId = this.getItem('activeBaseLayer');
      this.activeLayerNames = this.getItem('activeLayers');
    } else {
      centerMap = [52.1, 5];
      zoomLevel = 8;

      this.setItems(L.latLng(centerMap), zoomLevel);
    }
    this.map = L.map('map').setView(centerMap, zoomLevel);

    await this.initialiseLayers();
    this.addLayers();
    this.initialiseEventHandlers();

    this.loadingMap = false;
  }

  private async initialiseLayers() {
    this.defaultBaseLayer = L.tileLayer(this.mapService.osmTilesUrl, {
      maxZoom: 20,
      maxNativeZoom: 16,
      id: this.DEFAULT_BASE_LAYER_ID
    });
    this.hectoPuntenLayer = L.tileLayer(this.mapService.hectometreTilesUrl, { maxZoom: 19 });

    if (this.authorizationService.isLocationAdmin()) {
      this.wmsAllinearsLayer = this.mapService.getWMSLayer(this.ALL_LINEARS_LAYERNAME);
      this.wmsAllPointsLayer = this.mapService.getWMSLayer(this.ALL_POINTS_LAYERNAME);
      this.wmsAllFcdLinearsLayer = this.mapService.getWMSLayer(this.ALL_FCD_LINEARS_LAYERNAME);
    } else if (this.contract.contractType === ContractType.DataProvider) {
      this.wmsOtherPointsLayer = this.mapService.getWMSLayer(
        this.OTHER_POINTS_LAYERNAME,
        `contractid:${this.contractId}`
      );
      this.wmsContractPointsLayer = this.mapService.getWMSLayer(
        this.CONTRACT_POINTS_LAYERNAME,
        `contractid:${this.contractId}`
      );
      this.wmsOtherLinearsLayer = this.mapService.getWMSLayer(
        this.OTHER_LINEARS_LAYERNAME,
        `contractid:${this.contractId}`
      );
      this.wmsContractLinearsLayer = this.mapService.getWMSLayer(
        this.CONTRACT_LINEARS_LAYERNAME,
        `contractid:${this.contractId}`
      );
    } else if (this.contract.contractType === ContractType.FCD) {
      this.wmsOtherFcdLinearsLayer = this.mapService.getWMSLayer(
        this.OTHER_FCD_LINEARS_LAYERNAME,
        `contractid:${this.contractId}`
      );
      this.wmsContractLinearsLayer = this.mapService.getWMSLayer(
        this.CONTRACT_LINEARS_LAYERNAME,
        `contractid:${this.contractId}`
      );
    }
  }

  private addLayers() {
    this.map.addLayer(this.defaultBaseLayer);
    this.activeBaseLayerId = this.DEFAULT_BASE_LAYER_ID;

    this.setItem('activeBaseLayer');

    if (!this.activeLayerNames || this.activeLayerNames.length === 0) {
      this.map.addLayer(this.hectoPuntenLayer);
      this.activeLayerNames.push(this.HECTOPUNTEN_LAYERNAME);

      if (this.authorizationService.isLocationAdmin()) {
        this.map.addLayer(this.wmsAllinearsLayer);
        this.map.addLayer(this.wmsAllPointsLayer);
        this.map.addLayer(this.wmsAllFcdLinearsLayer);
        this.activeLayerNames.push(this.ALL_LINEARS_LAYERNAME);
        this.activeLayerNames.push(this.ALL_POINTS_LAYERNAME);
        this.activeLayerNames.push(this.ALL_FCD_LINEARS_LAYERNAME);
      } else if (this.contract.contractType === ContractType.DataProvider) {
        this.map.addLayer(this.wmsContractLinearsLayer);
        this.map.addLayer(this.wmsContractPointsLayer);
        this.activeLayerNames.push(this.CONTRACT_POINTS_LAYERNAME);
        this.activeLayerNames.push(this.CONTRACT_LINEARS_LAYERNAME);
      } else if (this.contract.contractType === ContractType.FCD) {
        this.map.addLayer(this.wmsContractLinearsLayer);
        this.activeLayerNames.push(this.CONTRACT_LINEARS_LAYERNAME);
      }
    } else {
      for (const activeLayerName of this.activeLayerNames) {
        switch (activeLayerName) {
          case this.HECTOPUNTEN_LAYERNAME:
            this.map.addLayer(this.hectoPuntenLayer);
            break;
          case this.ALL_LINEARS_LAYERNAME:
            this.map.addLayer(this.wmsAllinearsLayer);
            break;
          case this.ALL_POINTS_LAYERNAME:
            this.map.addLayer(this.wmsAllPointsLayer);
            break;
          case this.ALL_FCD_LINEARS_LAYERNAME:
            this.map.addLayer(this.wmsAllFcdLinearsLayer);
            break;
          case this.CONTRACT_LINEARS_LAYERNAME:
            this.map.addLayer(this.wmsContractLinearsLayer);
            break;
          case this.CONTRACT_POINTS_LAYERNAME:
            this.map.addLayer(this.wmsContractPointsLayer);
            break;
          case this.OTHER_LINEARS_LAYERNAME:
            this.map.addLayer(this.wmsOtherLinearsLayer);
            break;
          case this.OTHER_POINTS_LAYERNAME:
            this.map.addLayer(this.wmsOtherPointsLayer);
            break;
          case this.OTHER_FCD_LINEARS_LAYERNAME:
            this.map.addLayer(this.wmsOtherFcdLinearsLayer);
            break;
        }
      }
    }
    this.setItem('activeLayers');
  }

  private initialiseEventHandlers() {
    this.map.on('baselayerchange', (e: any) => {
      this.activeBaseLayerId = e.layer.options.id;
      this.setItem('activeBaseLayer');
    });

    this.map.on('overlayadd', (e: any) => {
      this.activeLayerNames.push(e.layer.options.layers);
      this.setItem('activeLayers');
    });

    this.map.on('overlayremove', (e: any) => {
      this.activeLayerNames.splice(this.activeLayerNames.indexOf(e.layer.options.layers), 1);
      this.setItem('activeLayers');
    });

    this.map.on('zoomend', (e: L.LeafletEvent) => this.setItem('zoomLevel'));

    this.map.on('click', (e: L.LeafletMouseEvent) => {
      const mapSize = this.map.getSize();
      const mapBounds = this.map.getBounds();
      const containerPoint = e.containerPoint;
      const latlng = e.latlng;

      const mapHeight = mapSize.y;
      const mapWidth = mapSize.x;
      const minY = mapBounds.getSouthWest().lng;
      const minX = mapBounds.getSouthWest().lat;
      const maxY = mapBounds.getNorthEast().lng;
      const maxX = mapBounds.getNorthEast().lat;
      const coordinaatX = Math.round(containerPoint.x);
      const coordinaatY = Math.round(containerPoint.y);

      if (this.activeLayerNames.length > 0) {
        this.mapService
          .getFeatureInfo(
            mapHeight,
            mapWidth,
            minY,
            minX,
            maxY,
            maxX,
            coordinaatX,
            coordinaatY,
            this.activeLayerNames,
            this.contractId
          )
          .subscribe(featureInfo => this.loadFeatureInfo(featureInfo, latlng));
      }

      this.setItems(latlng, this.map.getZoom());
    });
  }

  private initialiseLegend(): void {
    if (!this.legend) {
      this.legend = L.control({ position: 'bottomright' });
    } else {
      this.map.removeControl(this.legend);
    }

    this.legend.onAdd = e => {
      const div = L.DomUtil.create('div', 'info legend');
      const labels = [this.legendActiveTranslation, this.legendInacitveTranslation];
      const colors = ['#7FFF00', '#FF0000', '#606060'];

      if (!this.authorizationService.isLocationAdmin()) {
        labels.push(this.legendOthersTranslation);
      }
      // loop through our density intervals and generate a label with a colored square for each interval
      for (let i = 0; i < labels.length; i++) {
        div.innerHTML += `<i style="background:${colors[i]}"></i> ${labels[i]}<br />`;
      }
      return div;
    };
    this.legend.addTo(this.map);
  }

  private setItems(latlng: L.LatLng, zoomLevel: number): void {
    localStorage.setItem(
      StorageKey.MAP_OPTIONS,
      JSON.stringify({
        latlng,
        zoomLevel,
        activeBaseLayer: this.activeBaseLayerId,
        activeLayers: this.activeLayerNames
      })
    );
  }

  private setItem(item: string): void {
    const retrievedObject: { latlng: L.LatLng; zoomLevel: number; activeBaseLayer: string; activeLayers: string[] } =
      JSON.parse(localStorage.getItem(StorageKey.MAP_OPTIONS));
    switch (item) {
      case 'zoomLevel':
        retrievedObject.zoomLevel = this.map.getZoom();
        break;
      case 'activeBaseLayer':
        retrievedObject.activeBaseLayer = this.activeBaseLayerId;
        break;
      case 'activeLayers':
        retrievedObject.activeLayers = this.activeLayerNames;
        break;
    }
    localStorage.setItem(StorageKey.MAP_OPTIONS, JSON.stringify(retrievedObject));
  }

  public getItem(item: string): any {
    const retrievedObject: { latlng: L.LatLng; zoomLevel: number; activeBaseLayer: string; activeLayers: string[] } =
      JSON.parse(localStorage.getItem(StorageKey.MAP_OPTIONS));
    let value: any;
    switch (item) {
      case 'latlng':
        value = retrievedObject.latlng;
        break;
      case 'zoomLevel':
        value = retrievedObject.zoomLevel;
        break;
      case 'activeBaseLayer':
        value = retrievedObject.activeBaseLayer;
        break;
      case 'activeLayers':
        value = retrievedObject.activeLayers;
        break;
    }
    return value;
  }

  private initialiseControl(): void {
    if (!this.control) {
      this.control = L.control.layers({}, {}, { position: 'topright' }).addTo(this.map);
    } else {
      this.control.removeLayer(this.defaultBaseLayer);
      if (this.authorizationService.isLocationAdmin()) {
        if (this.authorizationService.isMapAdmin()) {
          this.alternativeBaseLayers.forEach(layer => this.control.removeLayer(layer));
        }
        this.control.removeLayer(this.wmsAllinearsLayer);
        this.control.removeLayer(this.wmsAllFcdLinearsLayer);
        this.control.removeLayer(this.wmsAllPointsLayer);
      } else if (this.contract.contractType === ContractType.DataProvider) {
        this.control.removeLayer(this.wmsOtherLinearsLayer);
        this.control.removeLayer(this.wmsOtherPointsLayer);
        this.control.removeLayer(this.wmsContractLinearsLayer);
        this.control.removeLayer(this.wmsContractPointsLayer);
      } else if (this.contract.contractType === ContractType.FCD) {
        this.control.removeLayer(this.wmsOtherFcdLinearsLayer);
        this.control.removeLayer(this.wmsContractLinearsLayer);
      }
      this.control.removeLayer(this.hectoPuntenLayer);
    }

    this.control.addBaseLayer(this.defaultBaseLayer, this.defaultBaseLayerTranslation);
    if (this.authorizationService.isLocationAdmin()) {
      if (this.authorizationService.isMapAdmin()) {
        this.alternativeBaseLayers.forEach(layer => this.control.addBaseLayer(layer, layer.options.id));
      }
      this.control.addOverlay(this.wmsAllinearsLayer, this.allLinearLayerNametranslation);
      this.control.addOverlay(this.wmsAllFcdLinearsLayer, this.allFcdLinearLayerNameTranslation);
      this.control.addOverlay(this.wmsAllPointsLayer, this.allPointLayerNameTranslation);
    } else if (this.contract.contractType === ContractType.DataProvider) {
      this.control.addOverlay(this.wmsOtherLinearsLayer, this.otherLinearLayerNameTranslation);
      this.control.addOverlay(this.wmsOtherPointsLayer, this.otherPointLayerNametranslation);
      this.control.addOverlay(this.wmsContractLinearsLayer, this.contractLinearLayerNameTranslation);
      this.control.addOverlay(this.wmsContractPointsLayer, this.contractPointLayerNameTranslation);
    } else if (this.contract.contractType === ContractType.FCD) {
      this.control.addOverlay(this.wmsOtherFcdLinearsLayer, this.otherFcdLinearLayerNameTranslation);
      this.control.addOverlay(this.wmsContractLinearsLayer, this.contractLinearLayerNameTranslation);
    }
    this.control.addOverlay(this.hectoPuntenLayer, this.hectopuntenLayerNameTranslation);
  }

  private loadFeatureInfo(featureInfo: any[], latlng: L.LatLng): void {
    if (featureInfo.length > 0) {
      const container = L.DomUtil.create('div');
      for (const info of featureInfo) {
        const id = info.properties.id;
        const active = info.properties.active;

        const location = this.locations.find(x => x.id === id);

        const row = L.DomUtil.create('div', 'row', container);
        const title = L.DomUtil.create('label', 'col-sm-8', row);
        title.innerHTML = location.recordId;

        const rowButtons = L.DomUtil.create('div', 'row', container);
        const divButtonLocationDetails = L.DomUtil.create('div', 'col-sm-3', rowButtons);
        const locationDetailsButton = L.DomUtil.create('button', '', divButtonLocationDetails);
        locationDetailsButton.setAttribute('type', 'button');
        if (active) {
          locationDetailsButton.setAttribute('class', 'btn btn-success btn-xs');
        } else {
          locationDetailsButton.setAttribute('class', 'btn btn-danger btn-xs');
        }
        locationDetailsButton.innerHTML = '<span class="glyphicon glyphicon-eye-open"></span>';

        L.DomEvent.on(locationDetailsButton, 'click', e => this.goToLocationdetailPage.emit(location));

        if (
          this.authorizationService.isLocationAdmin() ||
          (this.contract && this.contract.dataproviderType !== DataProviderType.MST)
        ) {
          const divButtonLocationEdit = L.DomUtil.create('div', 'col-sm-3', rowButtons);
          const locationEditButton = L.DomUtil.create('button', '', divButtonLocationEdit);
          locationEditButton.setAttribute('type', 'button');
          if (active) {
            locationEditButton.setAttribute('class', 'btn btn-success btn-xs');
          } else {
            locationEditButton.setAttribute('class', 'btn btn-danger btn-xs');
          }
          locationEditButton.innerHTML = '<span class="glyphicon glyphicon-pencil"></span>';

          L.DomEvent.on(locationEditButton, 'click', e => this.goToLocationEditPage.emit(location));
        }
      }
      L.popup().setLatLng(latlng).setContent(container).openOn(this.map);
    }
  }

  private initialiseLegendLabels(): void {
    this.translate('LOCATIONS.LEGEND.ACTIVE').subscribe(translation => (this.legendActiveTranslation = translation));

    this.translate('LOCATIONS.LEGEND.INACTIVE').subscribe(
      translation => (this.legendInacitveTranslation = translation)
    );

    this.translate('LOCATIONS.LEGEND.OTHERS').subscribe(translation => (this.legendOthersTranslation = translation));
  }

  private initialiseLayerNames(): void {
    this.translate('SITERECORD.DEFAULTBASELAYER').subscribe(
      translation => (this.defaultBaseLayerTranslation = translation)
    );

    this.translate('SITERECORD.HECTOPUNTENLAYER').subscribe(
      translation => (this.hectopuntenLayerNameTranslation = translation)
    );

    this.translate('SITERECORD.AllLINEARLAYERNAME').subscribe(
      translation => (this.allLinearLayerNametranslation = translation)
    );

    this.translate('SITERECORD.ALLFCDLINEARLAYERNAME').subscribe(
      translation => (this.allFcdLinearLayerNameTranslation = translation)
    );

    this.translate('SITERECORD.AllPOINTLAYERNAME').subscribe(
      translation => (this.allPointLayerNameTranslation = translation)
    );

    this.translate('SITERECORD.OTHERFCDLINEARLAYERNAME').subscribe(
      translation => (this.otherFcdLinearLayerNameTranslation = translation)
    );

    this.translate('SITERECORD.OTHERLINEARLAYERNAME').subscribe(
      translation => (this.otherLinearLayerNameTranslation = translation)
    );

    this.translate('SITERECORD.OTHERPOINTLAYERNAME').subscribe(
      translation => (this.otherPointLayerNametranslation = translation)
    );

    this.translate('SITERECORD.CONTRACTLINEARLAYERNAME').subscribe(
      translation => (this.contractLinearLayerNameTranslation = translation)
    );

    this.translate('SITERECORD.CONTRACTPOINTLAYERNAME').subscribe(
      translation => (this.contractPointLayerNameTranslation = translation)
    );
  }

  private translate(valueToTranslate: string): Observable<string> {
    return this.translateService.get(valueToTranslate);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/locations-map/locations-map.component.ts